import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { TOUR_STEPS, TOUR_UI } from "../lib/tourSteps";
import { X, ArrowRight, ArrowLeft, List, Check } from "lucide-react";
import { audio } from "../lib/audio";

const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const PAD = 10;
const CARD = "bg-[rgba(255,255,255,0.95)] backdrop-blur-[8px] border border-black/5 text-[#1a1a1a] shadow-2xl";

const isVisible = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 4 && r.height > 4 && el.offsetParent !== null;
};

export default function GuidedTour({ onClose }) {
  const { language, t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const ui = TOUR_UI[language] || TOUR_UI["हिंदी"];
  const L = language === "हिंदी" ? "hi" : "en";
  const [phase, setPhase] = useState("intro");
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const total = TOUR_STEPS.length;
  const step = TOUR_STEPS[i];

  const beginTour = () => { setPhase("tour"); audio.playTour(); };
  const finish = () => { audio.stopTour(); localStorage.setItem("am_tour_done", "1"); onClose?.(); };
  const next = () => { if (i >= total - 1) { audio.stopTour(); setPhase("finale"); } else setI(i + 1); };
  const back = () => setI((v) => Math.max(0, v - 1));
  const jumpTo = (idx) => { setI(idx); setDrawer(false); };

  // Navigate to the step's page + poll for its spotlight target
  useEffect(() => {
    if (phase !== "tour") return;
    let cancelled = false;
    if (location.pathname !== step.route) navigate(step.route);
    setRect(null);
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${step.sel}"]`);
      if (el && isVisible(el)) {
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center", inline: "nearest" });
        setTimeout(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
        }, reduce ? 0 : 300);
        return;
      }
      tries += 1;
      if (tries < 34) setTimeout(tick, 120);
      else setRect(null); // fallback: centred tooltip, no spotlight
    };
    const id = setTimeout(tick, 160);
    return () => { cancelled = true; clearTimeout(id); };
  }, [phase, i, location.pathname]);

  // keep spotlight aligned on scroll/resize
  useEffect(() => {
    if (phase !== "tour") return;
    const h = () => {
      const el = document.querySelector(`[data-testid="${step.sel}"]`);
      if (el && isVisible(el)) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
      }
    };
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [phase, i]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (phase === "tour" && e.key === "ArrowRight") next();
      else if (phase === "tour" && e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, i]);

  const tip = (() => {
    const vw = window.innerWidth, vh = window.innerHeight, W = 340, H = 232;
    if (!rect) return { top: Math.max(16, vh / 2 - H / 2), left: Math.max(12, vw / 2 - W / 2), centred: true };
    const below = rect.top + rect.height + 12;
    const showBelow = below + H < vh;
    return { top: showBelow ? below : Math.max(12, rect.top - H - 8), left: Math.min(Math.max(12, rect.left), vw - W - 12), centred: false };
  })();

  return (
    <div className="fixed inset-0 z-[10050]" data-testid="guided-tour">
      {/* INTRO */}
      {phase === "intro" && (
        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(8,22,14,0.5)] backdrop-blur-md px-6">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className={`${CARD} rounded-3xl p-7 max-w-sm text-center`} data-testid="tour-intro">
            <div className="text-4xl mb-3">🌾</div>
            <p className="text-xl font-bold mb-1">{ui.intro}</p>
            <p className="text-sm text-black/55 mb-5">20 {ui.steps} 20</p>
            <button onClick={beginTour} data-testid="tour-start" className="w-full py-3 rounded-2xl bg-green-600 text-white font-semibold hover:bg-green-700 tap">{ui.start}</button>
            <button onClick={finish} data-testid="tour-skip" className="mt-3 text-sm text-black/50">{ui.skip}</button>
          </motion.div>
        </div>
      )}

      {/* TOUR */}
      {phase === "tour" && (
        <>
          {/* fog + spotlight */}
          {rect ? (
            <motion.div className="absolute rounded-2xl pointer-events-none" initial={false}
              animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
              transition={{ duration: reduce ? 0 : 0.5, ease: [0.4, 0, 0.2, 1] }}
              style={{ boxShadow: "0 0 0 9999px rgba(8,22,14,0.64)", outline: "2px solid rgba(34,197,94,0.95)", outlineOffset: 2 }} />
          ) : (
            <div className="absolute inset-0 bg-[rgba(8,22,14,0.64)] backdrop-blur-[2px]" />
          )}

          {/* advance catcher */}
          <div className="absolute inset-0" onClick={next} data-testid="tour-advance" />

          {/* skip */}
          <button onClick={finish} data-testid="tour-skip" className={`absolute top-4 right-4 z-10 flex items-center gap-1 px-3 py-1.5 rounded-full ${CARD} text-sm tap`}><X className="h-4 w-4" />{ui.skip}</button>

          {/* step-list drawer chip */}
          <button onClick={(e) => { e.stopPropagation(); setDrawer((v) => !v); }} data-testid="tour-drawer-chip"
            className={`absolute bottom-5 left-5 z-20 flex items-center gap-2 px-3.5 py-2 rounded-full ${CARD} text-sm font-semibold tap`}>
            <List className="h-4 w-4" />{i + 1} {ui.steps} {total}
          </button>

          {/* drawer panel */}
          <AnimatePresence>
            {drawer && (
              <motion.div onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
                className={`absolute bottom-16 left-5 z-30 w-72 max-h-[60vh] overflow-y-auto ${CARD} rounded-2xl p-2`} data-testid="tour-drawer">
                <p className="px-3 py-2 text-xs font-bold text-black/50 uppercase tracking-wide">{ui.allSteps}</p>
                {TOUR_STEPS.map((s, idx) => (
                  <button key={idx} onClick={() => jumpTo(idx)} data-testid={`tour-jump-${idx}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-sm tap ${idx === i ? "bg-green-600 text-white" : "hover:bg-black/5 text-[#1a1a1a]"}`}>
                    <span className="text-base w-6 text-center">{s.emoji}</span>
                    <span className="flex-1 truncate">{t(s.nav)}</span>
                    {idx < i && <Check className="h-3.5 w-3.5 opacity-70" />}
                    <span className="text-[11px] opacity-60">{idx + 1}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* tooltip */}
          <AnimatePresence mode="wait">
            <motion.div key={i} onClick={(e) => e.stopPropagation()}
              initial={reduce ? { opacity: 0 } : { scale: 0.92, opacity: 0, y: 10 }}
              animate={reduce ? { opacity: 1 } : { scale: 1, opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className={`absolute w-[340px] max-w-[calc(100vw-24px)] ${CARD} rounded-3xl p-5 z-10`}
              style={{ top: tip.top, left: tip.left }} data-testid="tour-tooltip">
              <div className="flex items-center gap-2.5 mb-2.5">
                <span className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center text-xl shrink-0">{step.emoji}</span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-600 text-white">{i + 1} {ui.steps} {total}</span>
              </div>
              <p className="font-bold text-base mb-1 leading-snug">{step[L].t}</p>
              <p className="text-sm text-black/65 mb-4 leading-relaxed">{step[L].x}</p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 flex-wrap max-w-[140px]">
                  {TOUR_STEPS.map((_, d) => <span key={d} className={`h-1.5 rounded-full transition-all ${d === i ? "w-4 bg-green-600" : "w-1.5 bg-black/15"}`} />)}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {i > 0 && <button onClick={back} className="h-9 w-9 rounded-full border border-black/10 flex items-center justify-center tap" data-testid="tour-back"><ArrowLeft className="h-4 w-4" /></button>}
                  <button onClick={next} data-testid="tour-next" className="flex items-center gap-1 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 tap">{ui.next}<ArrowRight className="h-4 w-4" /></button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </>
      )}

      {/* FINALE */}
      {phase === "finale" && (
        <div className="absolute inset-0 flex items-center justify-center px-6 bg-[rgba(8,22,14,0.5)] backdrop-blur-md">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className={`${CARD} rounded-3xl p-7 max-w-sm text-center`} data-testid="tour-finale">
            <div className="text-4xl mb-3">🌾</div>
            <p className="text-2xl font-bold mb-2">{ui.finaleT}</p>
            <p className="text-sm text-black/65 mb-5 leading-relaxed">{ui.finaleX}</p>
            <button onClick={finish} data-testid="tour-finale-cta" className="w-full py-3 rounded-2xl bg-green-600 text-white font-semibold hover:bg-green-700 tap mb-2">{ui.finaleCta}</button>
            <button onClick={finish} data-testid="tour-stay" className="text-sm text-black/50">{ui.stay}</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
